import {
    useSensors,
    useSensor,
    PointerSensor,
    KeyboardSensor,
    DragEndEvent,
    DndContext,
    closestCenter,
} from "@dnd-kit/core";
import {
    useSortable,
    sortableKeyboardCoordinates,
    SortableContext,
    horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { styled, ToggleButton, Box } from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { FoodsToggleButtonType } from "@foodpilot/foods";
import type { ReactNode } from "react";

const ViewTab = styled(ToggleButton)(({ theme }) => ({
    textTransform: "none",
    background: theme.custom.grey["gradientLight"],
    color: theme.custom.grey[2800],
    fontSize: "0.75rem",
    padding: "8px 14px",
    fontWeight: 700,
    borderRadius: "8px",
    "&.Mui-selected": {
        color: "#FFF",
        background: theme.custom.grey["gradientDark"],
        "&:hover": {
            color: "#FFF",
            background: theme.custom.grey["gradientDark"],
        },
    },
    "&:hover": {
        background: theme.custom.grey[400],
    },
}));

type DraggableViewTabsProps = {
    buttons: FoodsToggleButtonType[];
    selectedId: string | undefined;
    onReorder: (fromIndex: number, toIndex: number) => void;
    leading?: ReactNode;
};

export const DraggableViewTabs = (props: DraggableViewTabsProps) => {
    const { buttons, selectedId, onReorder, leading } = props;

    const fixedButton = buttons[0];
    const draggableButtons = buttons.slice(1);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 8 },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = draggableButtons.findIndex((b) => b.id === active.id);
        const newIndex = draggableButtons.findIndex((b) => b.id === over.id);
        if (oldIndex === -1 || newIndex === -1) return;

        onReorder(oldIndex + 1, newIndex + 1);
    };

    const sortableIds = draggableButtons.map((b) => b.id);

    return (
        <Box display="flex" flexWrap="wrap" gap="8px" alignItems="center">
            {leading ??
                (fixedButton && (
                    <ViewTab
                        value={fixedButton.id}
                        selected={fixedButton.id === selectedId}
                        onClick={() => fixedButton.onClick?.()}
                    >
                        {fixedButton.label}
                    </ViewTab>
                ))}

            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={sortableIds} strategy={horizontalListSortingStrategy}>
                    {draggableButtons.map((button) => (
                        <SortableTab key={button.id} button={button} isSelected={button.id === selectedId} />
                    ))}
                </SortableContext>
            </DndContext>
        </Box>
    );
};

type SortableTabProps = {
    button: FoodsToggleButtonType;
    isSelected: boolean;
};

const SortableTab = (props: SortableTabProps) => {
    const { button, isSelected } = props;
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: button.id,
    });

    return (
        <Box
            ref={setNodeRef}
            style={{
                transform: CSS.Transform.toString(transform),
                transition,
                zIndex: isDragging ? 1 : 0,
            }}
            {...attributes}
            {...listeners}
            sx={{ touchAction: "none", cursor: isDragging ? "grabbing" : "grab" }}
        >
            <ViewTab
                value={button.id}
                selected={isSelected}
                onClick={() => button.onClick?.()}
                sx={{
                    opacity: isDragging ? 0.7 : 1,
                    pointerEvents: isDragging ? "none" : "auto",
                    gap: "4px",
                    display: "flex",
                    alignItems: "center",
                }}
            >
                <DragIndicatorIcon sx={{ fontSize: "14px", opacity: 0.5 }} />
                {button.label}
            </ViewTab>
        </Box>
    );
};
